<template>
  <div class="dropdown-box">
    <div class="dropdown-box__label">{{ label }}</div>
    <DropdownStandard
      :options="options"
      :searchType="searchType"
      :title="title"
      :multiSelect="presentAsMultiSelect"
      :isAllSelected="allSelected"
      :hasSearch="true"
      :disabled="disabled"
      @selectAll="selectAll"
      @select="select"
    />
  </div>
</template>

<script lang="ts" setup>
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  label: string
  searchType: string
  presentAsMultiSelect?: boolean
  options: string[]
  selections?: string[]
  nameMap?: Record<string, string>
  optionsWithData?: Set<string>
}
const props = withDefaults(defineProps<Props>(), {
  presentAsMultiSelect: true,
  optionsWithData: undefined,
  selections: () => [],
  nameMap: () => ({})
})
const { nameMap, selections } = toRefs(props)
const emits = defineEmits<{ (e: 'selectionChanged', value: string[]) }>()
const { t } = useI18n()

const allSelected = computed<boolean>(() => {
  const enabledCount = options.value.filter((x) => !x.disabled).length
  const selectedCount = options.value.filter((x) => x.selected).length
  return enabledCount === selectedCount
})

export type TitleMode = 'all' | 'single' | 'multiple' | 'none-single' | 'none-multiple' | 'disabled'

const titleMode = computed<TitleMode>(() => {
  const enabledOptions = options.value.filter((x) => !x.disabled)
  const selectedOptionCount = enabledOptions.filter((x) => x.selected).length
  if (allSelected.value && props.presentAsMultiSelect && !disabled.value) return 'all'
  if (selectedOptionCount > 1) return 'multiple'
  if (selectedOptionCount === 1) return 'single'
  if (!disabled.value && selectedOptionCount === 0) {
    return props.presentAsMultiSelect ? 'none-multiple' : 'none-single'
  }
  return 'disabled'
})

const title = computed<string>(() => {
  const mode = titleMode.value
  if (mode === 'all') {
    return t('general__text__all') + ' ' + t('general__text__chosen').toLowerCase()
  }
  if (mode === 'single') {
    return getLabel(selections.value[0])
  }
  if (mode === 'multiple') {
    return `${selections.value.length} ${t('general__text__chosen').toLowerCase()}`
  }
  if (mode === 'none-single') {
    return t('general__text__select') + ' ' + props.searchType
  }
  if (mode === 'none-multiple') {
    return (
      t('general__text__select') +
      ' ' +
      t('general__text__one').toLowerCase() +
      ' ' +
      t('general__text__or-more').toLowerCase()
    )
  }
  return ''
})

const disabled = computed<boolean>(() => {
  if (options.value.length === 0) {
    return true
  }
  if (options.value.every((entry) => entry.disabled)) {
    return true
  }
  return false
})

const getLabel = (key: string) => {
  return nameMap.value[key] ?? t(key)
}

const options = computed<optionType[]>(() => {
  const options: optionType[] = []
  for (const item of props.options) {
    options.push({
      id: item,
      name: getLabel(item),
      value: undefined,
      disabled: shouldBeDisabled(item),
      selected: selections.value.includes(item)
    })
  }
  options.sort(sortOption)
  return options
})

function select(id: string) {
  const index = selections.value.findIndex((x) => x === id)
  if (index === -1) {
    if (!props.presentAsMultiSelect) {
      emits('selectionChanged', [id])
    } else {
      emits('selectionChanged', [...selections.value, id])
    }
  } else {
    const copy = [...selections.value]
    copy.splice(index, 1)
    emits('selectionChanged', copy)
  }
}
function selectAll() {
  if (!allSelected.value) {
    const selection = options.value.map((x) => x.id)
    emits('selectionChanged', selection)
  } else {
    emits('selectionChanged', [])
  }
}

function shouldBeDisabled(id: string): boolean {
  return !!props.optionsWithData && !props.optionsWithData.has(id)
}
function sortOption(a: optionType, b: optionType) {
  if (a.disabled === b.disabled) {
    return a.name.localeCompare(b.name)
  }
  return a.disabled < b.disabled ? -1 : 1
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.dropdown-box {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
  }
}
</style>
